import React, { useState, useEffect, useMemo } from 'react';
import { targetAPI, itemsAPI, sourcingAPI } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { formatCurrency, formatDate } from '../lib/utils';
import { Button } from '../components/ui/button';
import { HorizontalScrollTable } from '../components/ui/horizontal-scroll-table';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { format } from 'date-fns';
import {
  Plus, Loader2, Target, Search, ArrowUpDown, ArrowUp, ArrowDown,
  History, Edit, Trash2, Upload
} from 'lucide-react';
import AddTargetModal from '../components/targets/AddTargetModal';
import EditTargetModal from '../components/targets/EditTargetModal';
import TargetHistoryModal from '../components/targets/TargetHistoryModal';
import ImportTargetsModal from '../components/targets/ImportTargetsModal';

export default function TargetsPage() {
  const [targets, setTargets] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modalOpen, setModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [historyModalOpen, setHistoryModalOpen] = useState(false);
  const [importModalOpen, setImportModalOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortField, setSortField] = useState('sku_code');
  const [sortOrder, setSortOrder] = useState('asc');
  const { user, hasPermission } = useAuth();
  const canEdit = hasPermission('create_edit_targets');

  const [editingTarget, setEditingTarget] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [historyItem, setHistoryItem] = useState(null);
  const [historyData, setHistoryData] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [importFile, setImportFile] = useState(null);
  const [importJobId, setImportJobId] = useState(null);
  const [importJob, setImportJob] = useState(null);
  const [importProcessing, setImportProcessing] = useState(false);
  const [importElapsed, setImportElapsed] = useState(0);
  const [matchRow, setMatchRow] = useState(null);
  const [matchSearch, setMatchSearch] = useState('');
  const [recentJob, setRecentJob] = useState(null);
  const [hotTiers, setHotTiers] = useState({});

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const [targetRes, itemsRes] = await Promise.all([targetAPI.list(), itemsAPI.list({ limit: 5000 })]);
        setTargets(targetRes.data);
        setItems(itemsRes.data.items || []);
        const skuIds = targetRes.data.map((t) => t.sku_code).filter(Boolean);
        if (skuIds.length > 0) {
          // Read-only Sourcing "Hot" column — degrades silently if the caller has no
          // Sourcing access, the Targets table itself never depends on this succeeding.
          sourcingAPI.hotTiers(skuIds).then((res) => setHotTiers(res.data.tiers || {})).catch(() => {});
        }
      } catch (error) { toast.error('Failed to load data'); }
      finally { setLoading(false); }
    };
    fetchData();
  }, []);

  const availableItems = useMemo(() => {
    const userRoles = user?.roles || [];
    const isProductManager = userRoles.includes('product_manager') && !userRoles.includes('super_admin') && !userRoles.includes('product_admin');
    if (isProductManager) return items.filter(item => item.assigned_to === user?.id);
    return items;
  }, [items, user]);

  const handleSubmit = async ({ selectedItems, itemTargets, effectiveDate, notes }) => {
    setSubmitting(true);
    try {
      const promises = selectedItems.map(sku => {
        const t = itemTargets[sku];
        return targetAPI.create({ sku_id: sku, target_price_usd: parseFloat(t.target_price), expected_qty_for_period: t.expected_qty ? parseInt(t.expected_qty) : null, effective_date: effectiveDate, notes });
      });
      await Promise.all(promises);
      toast.success(`Created ${selectedItems.length} target(s)`);
      setModalOpen(false);
      const res = await targetAPI.list();
      setTargets(res.data);
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to create targets'); }
    finally { setSubmitting(false); }
  };

  const handleEditTarget = (target) => {
    setEditingTarget(target);
    setEditForm({ target_price_usd: target.target_price_usd || '', expected_qty_for_period: target.expected_qty_for_period || '', effective_date: target.effective_date || format(new Date(), 'yyyy-MM-dd'), notes: target.notes || '' });
    setEditModalOpen(true);
  };

  const handleUpdateTarget = async () => {
    if (!editingTarget) return;
    setSubmitting(true);
    try {
      await targetAPI.update(editingTarget.id, { sku_id: editingTarget.sku_id, target_price_usd: parseFloat(editForm.target_price_usd), expected_qty_for_period: editForm.expected_qty_for_period ? parseInt(editForm.expected_qty_for_period) : null, effective_date: editForm.effective_date, notes: editForm.notes });
      toast.success('Target updated');
      setEditModalOpen(false);
      const res = await targetAPI.list();
      setTargets(res.data);
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to update target'); }
    finally { setSubmitting(false); }
  };

  const handleDeleteTarget = async (targetId) => {
    if (!window.confirm('Delete this target?')) return;
    try { await targetAPI.delete(targetId); toast.success('Target deleted'); const res = await targetAPI.list(); setTargets(res.data); }
    catch (error) { toast.error(error.response?.data?.detail || 'Failed to delete target'); }
  };

  const handleViewHistory = async (target) => {
    setHistoryItem(target); setHistoryLoading(true); setHistoryModalOpen(true);
    try { const res = await targetAPI.getHistory(target.sku_id); setHistoryData(res.data || []); }
    catch (error) { toast.error('Failed to load history'); setHistoryData([]); }
    finally { setHistoryLoading(false); }
  };

  const handleDownloadSample = async () => {
    try {
      const response = await targetAPI.downloadSample();
      const blob = new Blob([response.data], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a'); link.href = url; link.download = 'target_prices_template.xlsx';
      document.body.appendChild(link); link.click(); document.body.removeChild(link);
      toast.success('Sample file downloaded');
    } catch (error) { toast.error('Failed to download sample'); }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) { if (!file.name.endsWith('.xlsx')) { toast.error('Please select an Excel file (.xlsx)'); return; } setImportFile(file); setImportJob(null); }
  };

  const pollJob = (jobId) => {
    setImportProcessing(true); setImportElapsed(0);
    let ticks = 0;
    const poll = setInterval(async () => {
      ticks += 1; setImportElapsed(ticks * 3);
      try {
        const status = await targetAPI.getImportStatus(jobId);
        if (status.data.status === 'completed') {
          clearInterval(poll);
          setImportJob(status.data); setImportProcessing(false);
          const s = status.data.stats || {};
          toast.success(`Processed ${s.total || 0} rows — ${s.created || 0} created, ${s.updated || 0} updated, ${s.needs_review || 0} need review`);
          if ((s.created || 0) > 0 || (s.updated || 0) > 0) { const res = await targetAPI.list(); setTargets(res.data); }
          return;
        } else if (status.data.status === 'failed') {
          clearInterval(poll); setImportProcessing(false);
          toast.error(`Import failed: ${status.data.error || 'Unknown error'}`);
          return;
        }
      } catch (e) { /* keep polling */ }
      if (ticks >= 400) { // ~20 min ceiling — job keeps running server-side even if we stop polling
        clearInterval(poll); setImportProcessing(false);
        toast.error('This is taking longer than expected. It may still be finishing in the background — please reopen Import to check again.');
      }
    }, 3000);
  };

  const handleProcessImport = async () => {
    if (!importFile) return;
    setImportJob(null); setRecentJob(null);
    try {
      const r = await targetAPI.processImport(importFile);
      setImportJobId(r.data.job_id);
      pollJob(r.data.job_id);
    } catch (error) { toast.error(error.response?.data?.detail || 'Import failed'); }
  };

  const handleOpenImport = async () => {
    setImportModalOpen(true);
    try {
      const r = await targetAPI.getRecentImport();
      setRecentJob(r.data && r.data.id ? r.data : null);
    } catch (e) { setRecentJob(null); }
  };

  const handleResumeRecent = () => {
    if (!recentJob) return;
    setImportJobId(recentJob.id);
    if (recentJob.status === 'processing') {
      pollJob(recentJob.id);
    } else {
      setImportJob(recentJob);
      if (recentJob.status === 'failed') toast.error(`Import failed: ${recentJob.error || 'Unknown error'}`);
    }
    setRecentJob(null);
  };

  const handleApplyMatch = async (row, sku) => {
    try {
      const r = await targetAPI.applyImportMatch({ job_id: importJobId, sheet: row.sheet, row_num: row.row_num, sku, price: row.price });
      setImportJob((prev) => ({
        ...prev,
        results: prev.results.map((x) => (x.sheet === row.sheet && x.row_num === row.row_num)
          ? { ...x, matched_sku: sku, matched_name: r.data.name, action: 'AUTO_APPLIED', match_type: 'MANUAL', confidence: 100 }
          : x),
      }));
      setMatchRow(null); setMatchSearch('');
      toast.success(`Matched to ${sku}`);
      const res = await targetAPI.list(); setTargets(res.data);
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to apply match'); }
  };

  const handleResetImport = () => {
    setImportModalOpen(false); setImportFile(null); setImportJob(null); setImportJobId(null);
    setImportProcessing(false); setImportElapsed(0); setMatchRow(null); setMatchSearch(''); setRecentJob(null);
  };

  const handleSort = (field) => {
    if (sortField === field) setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
    else { setSortField(field); setSortOrder('asc'); }
  };

  const sortedTargets = useMemo(() => {
    let filtered = [...targets];
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(t => (t.sku_code && t.sku_code.toLowerCase().includes(query)) || (t.item_name && t.item_name.toLowerCase().includes(query)) || (t.notes && t.notes.toLowerCase().includes(query)));
    }
    return filtered.sort((a, b) => {
      const aVal = a[sortField] || ''; const bVal = b[sortField] || '';
      const cmp = String(aVal).localeCompare(String(bVal), undefined, { numeric: true });
      return sortOrder === 'asc' ? cmp : -cmp;
    });
  }, [targets, searchQuery, sortField, sortOrder]);

  const SortIcon = ({ field }) => {
    if (sortField !== field) return <ArrowUpDown className="h-3 w-3 ml-1 opacity-50" />;
    return sortOrder === 'asc' ? <ArrowUp className="h-3 w-3 ml-1 text-primary" /> : <ArrowDown className="h-3 w-3 ml-1 text-primary" />;
  };

  const SortableHeader = ({ field, children }) => (
    <TableHead className="font-medium text-xs uppercase cursor-pointer hover:bg-muted/80 select-none" onClick={() => handleSort(field)}>
      <div className="flex items-center">{children}<SortIcon field={field} /></div>
    </TableHead>
  );

  return (
    <div className="space-y-6" data-testid="targets-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2"><Target className="h-6 w-6 text-primary" />Targets</h1>
          <p className="text-muted-foreground">Manage item target prices</p>
        </div>
        {canEdit && (
          <div className="flex gap-2">
            <Button variant="outline" onClick={handleOpenImport} className="rounded-sm"><Upload className="h-4 w-4 mr-2" />Import Excel</Button>
            <Button onClick={() => setModalOpen(true)} className="rounded-sm" data-testid="add-target-btn"><Plus className="h-4 w-4 mr-2" />Add Target</Button>
          </div>
        )}
      </div>

      <Card className="rounded-sm">
        <CardContent className="p-4">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input placeholder="Search by SKU, name, or notes..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-10 rounded-sm" data-testid="search-targets" />
          </div>
        </CardContent>
      </Card>

      <Card className="rounded-sm">
        <CardContent className="p-0">
          <HorizontalScrollTable>
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/50">
                <SortableHeader field="sku_code">SKU</SortableHeader>
                <SortableHeader field="item_name">Item Name</SortableHeader>
                <SortableHeader field="target_price_usd">Target Price</SortableHeader>
                <SortableHeader field="expected_qty_for_period">Expected Qty</SortableHeader>
                <SortableHeader field="effective_date">Effective Date</SortableHeader>
                {Object.keys(hotTiers).length > 0 && <TableHead className="font-medium text-xs uppercase">Hot</TableHead>}
                <TableHead className="font-medium text-xs uppercase">Notes</TableHead>
                {canEdit && <TableHead className="font-medium text-xs uppercase w-28">Actions</TableHead>}
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={canEdit ? 7 : 6} className="text-center py-8"><Loader2 className="h-6 w-6 animate-spin mx-auto text-muted-foreground" /></TableCell></TableRow>
              ) : sortedTargets.length === 0 ? (
                <TableRow><TableCell colSpan={canEdit ? 7 : 6} className="text-center py-8"><Target className="h-12 w-12 mx-auto text-muted-foreground/50 mb-2" /><p className="text-muted-foreground">No targets found</p></TableCell></TableRow>
              ) : (
                sortedTargets.map((target) => (
                  <TableRow key={target.id} className="table-row-hover">
                    <TableCell className="font-mono font-medium">{target.sku_code}</TableCell>
                    <TableCell className="max-w-[200px] truncate text-muted-foreground">{target.item_name || '-'}</TableCell>
                    <TableCell className="font-mono font-semibold text-emerald-600">{formatCurrency(target.target_price_usd)}</TableCell>
                    <TableCell className="font-mono">{target.expected_qty_for_period || '-'}</TableCell>
                    <TableCell>{formatDate(target.effective_date)}</TableCell>
                    {Object.keys(hotTiers).length > 0 && (
                      <TableCell>
                        {(hotTiers[target.sku_code] === 'HOT' || hotTiers[target.sku_code] === 'WATCH') ? (
                          <Badge variant="outline" data-testid={`target-hot-badge-${target.sku_code}`}
                            className={hotTiers[target.sku_code] === 'HOT' ? 'bg-red-600/15 text-red-600 border-red-600/30' : 'bg-amber-500/15 text-amber-600 border-amber-500/30'}>
                            {hotTiers[target.sku_code]}
                          </Badge>
                        ) : '-'}
                      </TableCell>
                    )}
                    <TableCell className="text-muted-foreground max-w-[150px] truncate">{target.notes || '-'}</TableCell>
                    {canEdit && (
                      <TableCell>
                        <div className="flex gap-1">
                          <Button variant="ghost" size="sm" onClick={() => handleViewHistory(target)} className="h-8 w-8 p-0" title="View History"><History className="h-4 w-4" /></Button>
                          <Button variant="ghost" size="sm" onClick={() => handleEditTarget(target)} className="h-8 w-8 p-0" title="Edit"><Edit className="h-4 w-4" /></Button>
                          <Button variant="ghost" size="sm" onClick={() => handleDeleteTarget(target.id)} className="h-8 w-8 p-0 text-destructive hover:text-destructive" title="Delete"><Trash2 className="h-4 w-4" /></Button>
                        </div>
                      </TableCell>
                    )}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          </HorizontalScrollTable>
        </CardContent>
      </Card>

      <AddTargetModal open={modalOpen} onClose={() => setModalOpen(false)} items={availableItems} onSubmit={handleSubmit} submitting={submitting} />
      <EditTargetModal open={editModalOpen} onClose={() => setEditModalOpen(false)} target={editingTarget} editForm={editForm} setEditForm={setEditForm} onSave={handleUpdateTarget} submitting={submitting} />
      <TargetHistoryModal open={historyModalOpen} onClose={() => setHistoryModalOpen(false)} historyItem={historyItem} historyData={historyData} loading={historyLoading} />
      <ImportTargetsModal
        open={importModalOpen}
        onClose={handleResetImport}
        importFile={importFile}
        onFileSelect={handleFileSelect}
        onDownloadSample={handleDownloadSample}
        processing={importProcessing}
        elapsed={importElapsed}
        job={importJob}
        onProcess={handleProcessImport}
        onReset={handleResetImport}
        items={items}
        matchRow={matchRow}
        setMatchRow={setMatchRow}
        matchSearch={matchSearch}
        setMatchSearch={setMatchSearch}
        onApplyMatch={handleApplyMatch}
        recentJob={recentJob}
        onResumeRecent={handleResumeRecent}
        onDismissRecent={() => setRecentJob(null)}
      />
    </div>
  );
}
